import React, { useEffect, useMemo, useState } from "react";

import {
    Card,
    Table,
    Button,
    Input,
    Space,
    Typography,
    Tag,
    Tooltip,
    Empty,
    Spin,
    Modal
} from "antd";

import {
    BookOutlined,
    SearchOutlined,
    DeleteOutlined,
    ExclamationCircleFilled
} from "@ant-design/icons";

import Swal from "sweetalert2";

import api from "../../../api/api";

const { Title, Text } = Typography;
const { confirm } = Modal;

const SubjectViewList = () => {

    const token = localStorage.getItem("access");

    const [subjects, setSubjects] = useState([]);

    const [loading, setLoading] = useState(true);

    const [searchText, setSearchText] = useState("");



    // ---------------------------------------
    // Fetch Subjects
    // ---------------------------------------

    const fetchSubjects = async () => {

        if (!token) return;

        setLoading(true);

        try {

            const config = {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            };

            const response = await api.get(
                "api/users/subjects/",
                config
            );

            setSubjects(response.data);

        } catch (error) {

            console.error(error);

            Swal.fire({
                icon: "error",
                title: "Failed",
                text: "Unable to load subjects."
            });

        } finally {

            setLoading(false);

        }

    };



    useEffect(() => {

        fetchSubjects();

    }, []);




    // ---------------------------------------
    // Delete Subject
    // ---------------------------------------

    const handleDelete = (id) => {

        confirm({

            title: "Delete Subject",

            icon: <ExclamationCircleFilled />,

            content:
                "Are you sure you want to delete this subject? This action cannot be undone.",

            okText: "Delete",

            okType: "danger",

            cancelText: "Cancel",

            centered: true,

            async onOk() {

                try {

                    const config = {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    };

                    await api.delete(
                        `api/users/subjects/${id}/`,
                        config
                    );

                    setSubjects((previous) =>
                        previous.filter(
                            (item) => item.id !== id
                        )
                    );

                    Swal.fire({

                        icon: "success",

                        title: "Deleted",

                        text: "Subject deleted successfully.",

                        timer: 1800,

                        showConfirmButton: false

                    });

                } catch (error) {

                    Swal.fire({

                        icon: "error",

                        title: "Delete Failed",

                        text: "Unable to delete subject."

                    });

                }

            }

        });

    };




    // ---------------------------------------
    // Search Filter
    // ---------------------------------------

    const filteredSubjects = useMemo(() => {

        const keyword = searchText.toLowerCase();

        return subjects.filter((item) =>

            item.name
                ?.toLowerCase()
                .includes(keyword)

            ||

            item.division_assigned_name?.name
                ?.toLowerCase()
                .includes(keyword)

            ||

            String(item.id).includes(keyword)

        );

    }, [subjects, searchText]);
    // ---------------------------------------
    // Table Columns
    // ---------------------------------------

    const columns = [

        {
            title: "ID",

            dataIndex: "id",

            key: "id",

            width: 90,

            align: "center",

            sorter: (a, b) => a.id - b.id,


            render: (id) => (

                <Tag
                    color="geekblue"
                    style={{
                        minWidth: 45,
                        textAlign: "center",
                        fontWeight: 600
                    }}
                >

                    {id}

                </Tag>

            )

        },



        {
            title: "Subject Name",

            dataIndex: "name",

            key: "name",

            sorter: (a, b) =>
                a.name.localeCompare(b.name),


            render: (name) => (

                <Space>

                    <BookOutlined
                        style={{
                            color:"#800000"
                        }}
                    />

                    <Text strong>
                        {name}
                    </Text>

                </Space>

            )

        },



        {
            title: "Division Assigned",

            key: "division",


            sorter: (a, b) =>
                (
                    a.division_assigned_name?.name || ""
                )
                .localeCompare(
                    b.division_assigned_name?.name || ""
                ),


            render: (_, record) => (

                record.division_assigned_name ? (

                    <Tag

                        color="green"

                        style={{

                            borderRadius:20,

                            padding:"3px 14px",

                            fontWeight:500

                        }}

                    >

                        {record.division_assigned_name.name}

                    </Tag>

                )

                :

                (

                    <Tag color="default">

                        No Division

                    </Tag>

                )

            )

        },




        {
            title:"Actions",

            key:"actions",

            width:150,

            align:"center",


            render:(_, record)=>(


                <Tooltip title="Delete Subject">


                    <Button

                        danger

                        type="primary"

                        icon={<DeleteOutlined/>}

                        onClick={() =>
                            handleDelete(record.id)
                        }

                    >

                        Delete

                    </Button>


                </Tooltip>


            )

        }

    ];






    // ---------------------------------------
    // Pagination Configuration
    // ---------------------------------------

    const paginationConfig = {

        pageSize:8,

        showSizeChanger:true,

        pageSizeOptions:[
            "5",
            "8",
            "10",
            "20"
        ],

        showQuickJumper:true,


        showTotal:(total, range)=>

            `${range[0]}-${range[1]} of ${total} Subjects`

    };






    // ---------------------------------------
    // Search Component
    // ---------------------------------------

    const SearchBar = () => (

        <Input

            allowClear

            size="large"

            prefix={
                <SearchOutlined/>
            }


            placeholder="Search subject or division..."


            value={searchText}


            onChange={(e)=>
                setSearchText(e.target.value)
            }


            style={{

                width:350,

                borderRadius:8

            }}

        />

    );
    return (

        <div
            style={{
                padding:24,
                background:"#f5f6fa"
            }}
        >


            <Card

                bordered={false}

                style={{

                    borderRadius:12,

                    overflow:"hidden",

                    boxShadow:
                    "0 8px 24px rgba(0,0,0,.08)"

                }}


                bodyStyle={{

                    padding:20

                }}


            >


                {/* Header */}

                <div

                    style={{

                        background:"#fc8b00",

                        color:"#ffffff",

                        margin:"-20px -20px 20px -20px",

                        padding:"18px 24px",

                        display:"flex",

                        alignItems:"center",

                        justifyContent:"space-between"

                    }}

                >


                    <Space size="middle">


                        <BookOutlined

                            style={{

                                fontSize:28,

                                color:"#ffffff"

                            }}

                        />


                        <div>


                            <Title

                                level={3}

                                style={{

                                    margin:0,

                                    color:"#ffffff"

                                }}

                            >

                                Subject List


                            </Title>



                            <Text

                                style={{

                                    color:"#f5f5f5"

                                }}

                            >

                                Manage all school subjects


                            </Text>


                        </div>


                    </Space>



                </div>





                {/* Search */}

                <div

                    style={{

                        marginBottom:12

                    }}

                >

                    <SearchBar />


                </div>






                {/* Total Subjects Badge */}

                <div

                    style={{

                        marginBottom:20

                    }}

                >


                    <Tag

                        style={{

                            background:"#ffaa00",

                            color:"#000000",

                            border:"none",

                            borderRadius:20,

                            padding:"6px 16px",

                            fontSize:14,

                            fontWeight:600

                        }}

                    >

                        Total Subjects : {filteredSubjects.length}


                    </Tag>



                </div>







                {/* Table */}


                <Spin

                    spinning={loading}

                    size="large"

                >


                    <Table


                        rowKey="id"


                        columns={columns}


                        dataSource={filteredSubjects}


                        pagination={paginationConfig}


                        bordered



                        scroll={{

                            x:700

                        }}



                        locale={{

                            emptyText:(


                                <Empty

                                    image={
                                        Empty.PRESENTED_IMAGE_SIMPLE
                                    }

                                    description=
                                    "No Subjects Available"

                                />


                            )

                        }}



                        rowClassName={(_,index)=>

                            index % 2 === 0

                            ?

                            "table-row-light"

                            :

                            "table-row-white"

                        }


                    />


                </Spin>



            </Card>







            {/* Styling */}

            <style>

            {`

                .ant-table-thead > tr > th{

                     background: #000000 !important;
                     color: #ffffff !important;
                     font-weight:600 !important;
                     text-align:center;
                     }



                .ant-table-tbody > tr.table-row-light{

                    background:#fafafa;

                }



                .ant-table-tbody > tr.table-row-white{

                    background:#ffffff;

                }



                .ant-table-tbody > tr:hover > td{

                    background:#eeeeee !important;

                }



                .ant-card{

                    border-radius:12px;

                }



                @media(max-width:768px){


                    .ant-table{

                        font-size:13px;

                    }


                }


            `}

            </style>


        </div>

    );

};

export default SubjectViewList;

